<template>
    <div>
        <v-autocomplete
            v-on="$listeners"
            v-bind="attrs"
            :loading="firstLoad && !mergeItems.length || fetchingCache"
            ref="select"
            :search-input.sync="search"
            :no-filter="true"
            @click:clear="onClickClear"
            :return-object="returnObject"
        >
            <template v-if="customSelection" v-slot:selection="{ item, on, attrs }">
                <slot name="selection" v-bind="{ item, on, attrs }"></slot>
            </template>

            <template v-if="customItem" v-slot:item="{ item, on, attrs }">
                <slot name="item" v-bind="{ item, on, attrs }"></slot>
            </template>
            <template v-if="multiple" #prepend-item>
                <v-list-item
                    ripple
                    @mousedown.prevent
                    @click="selectAllItems"
                >
                    <v-list-item-action>
                        <v-icon :color="attrs.items.length > 0 ? 'indigo darken-4' : ''">
                            {{ icon }}
                        </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title>
                            Selecionar todos
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
            </template>

            <template #append-item>
                <SimpleLoading
                    v-if="!isFirstPage && hasNextPage && expanded || firstLoad && mergeItems.length"
                    :infinite-scroll="infiniteScroll"
                ></SimpleLoading>
            </template>

            <template #no-data>
                <div :class="['text-center text-caption px-4', { 'pb-2': firstLoad && mergeItems.length }]">
                    <template v-if="firstLoad && !mergeItems.length">Carregando opções...</template>
                    <template v-else>Nenhuma opção encontrada.</template>
                </div>
            </template>

            <template #append-outer>
                <slot name="append-outer" />
            </template>
        </v-autocomplete>
        <input
            v-for="(value, index) in hiddenValues"
            :key="index"
            type="hidden"
            :name="hiddenName"
            :value="value"
        >
    </div>
</template>

<script>
import SimpleLoading from './support/SimpleLoading';

export default {
    inheritAttrs: false,
    components: {
        SimpleLoading,
    },
    props: {
        name: {
            type: String,
            required: true,
        },
        itemValue: {
            type: String,
            required: true,
        },
        multiple: {
            type: Boolean,
            required: false,
            default: false,
        },
        config: {
            type: Object,
            required: true,
            validator: (value) =>
                typeof value.route === 'string' &&
                ( !value.filters || typeof value.filters === 'object' ) &&
                ( !value.searchField || typeof value.searchField === 'string' ),
        },
        persistent: {
            type: Boolean,
            required: false,
            default: true,
        },
        customSelection: {
            type: Boolean,
            require: false,
            default: false
        },
        customItem: {
            type: Boolean,
            require: false,
            default: false
        },
        returnObject: {
            type: Boolean,
            require: false,
            default: true
        },
        ignoreItem: {
            require: false
        }
    },
    data: () => ({
        hiddenValues: [],
        currentPage: 0,
        lastPage: 1,
        firstLoad: false,
        items: [],
        expanded: false,
        fetchingItems: false,
        search: null,
        rootSearch: null,
        cache: [],
        fetchingCache: false,
        triedRecoverIds: false,
        refMenu: undefined,
        isCached: false,
        debPreSearch: null,
    }),
    created() {
        this.debPreSearch = _.debounce(() => {
            this.rootSearch = this.search;
        }, 500);
    },
    mounted() {
        this.$set(this.$data, 'refMenu', this.$refs.select);
    },
    methods: {
        async onOpen() {
            this.expanded = true;

            if (!this.isFirstPage || this.firstLoad) return;
            this.fetchItems();
        },
        async onClose() {
            await new Promise(resolve => {
                while(!this.isCached) {};
                resolve();
            });

            this.expanded = false;
        },
        async onClickClear() {
            this.resetSelectOptions();

            this.$nextTick(() => {
                this.resetItems();
                this.fetchItems();
            });
        },
        async infiniteScroll([{ isIntersecting, target }]) {
            if (!isIntersecting || this.fetchingItems) return;

            const ul = target.offsetParent;
            const scrollTop = ul.scrollTop;

            this.fetchItems(async () => {
                await this.$nextTick();
                ul && (ul.scrollTop = scrollTop);
            });
        },
        async fetchItems(callback = () => {}) {
            if (this.isFirstPage) this.firstLoad = true;
            this.fetchingItems = true;

            const searchField = this.config.searchField || 'search';
            const search = this.rootSearch;
            const page = this.currentPage + 1;

            const isInvalidResponse = () => {
                return this.rootSearch !== search ||
                !this.expanded && !this.persistent ||
                (this.currentPage + 1) !== page;
            };

            axios.get(this.route(this.config.route, {
                    ...{
                        'page': page,
                        [searchField]: search,
                    },
                    ...(this.config.filters || {}),
                })
            )
            .then((response) => {
                if(isInvalidResponse()) return false;

                const data = response.data;

                let dataItems = this.ignoreItem
                    ? _.filter(data.data, (item) => { return item[this.itemValue] != this.ignoreItem})
                    : data.data

                this.items.push(...dataItems);
                this.lastPage = data.last_page;
                this.currentPage++;

                return true;
            })
            .catch(() => {})
            .then((response) => {
                if(!response) return;

                this.firstLoad = false;
                this.fetchingItems = false;
                callback();
            });
        },
        setHiddenValues(values) {
            this.isCached = false;

            if (!Array.isArray(values)) values = [values];
            let arr = [];

            values.forEach(value => {
                if (['string', 'number'].includes(typeof value)) {
                    arr.push(value);
                } else if (value && typeof value == 'object' && value.hasOwnProperty('id')) {
                    if (value.id > 0) arr.push(value.id);
                } else { return }
            });

            if (arr.length === 0 && !this.multiple) arr.push(null);

            if (_.isEqual(arr, this.hiddenValues)) this.isCached = true;
            else {
                this.hiddenValues = arr
                this.updateCache()
            };
        },
        updateCache() {
            let notFoundInItems = []

            this.hiddenValues.forEach((value) => {
                if (
                    value === null ||
                    this.cache.findIndex(item => item[this.itemValue] === value) !== -1
                ) return

                const item = this.items.find(item => item[this.itemValue] === value)

                if(!item) notFoundInItems.push(value)
                else this.cache.push(_.cloneDeep(item))
            })

            this.checkCache()
            this.fetchCache(notFoundInItems)

            this.isCached = true
        },
        resetItems() {
            this.items = []
            this.currentPage = 0;
            this.lastPage = 1;
        },
        resetSelectOptions() {
            this.triedRecoverIds = false;
            this.cache = [];
            this.search = null;
            this.rootSearch = null;
        },
        checkCache() {
            this.cache = this.cache.filter((item) => {
                return this.hiddenValues.findIndex(value => value == item[this.itemValue]) !== -1
            })
        },
        async fetchCache(ids = []) {
            if (
                ids.length === 0 ||
                this.fetchingCache ||
                this.triedRecoverIds
            ) return

            this.triedRecoverIds = true
            this.fetchingCache = true

            axios.get(this.route(this.config.route, {
                    ...{ ids },
                    ...(this.config.filters || {}),
                })
            )
            .then((response) => {
                const data = response.data
                this.cache.push(...data.data)

                this.checkCache()
            })
            .catch(() => {
                this.$bus.$emit('cp-select-failed', this.name)
            })
            .then(() => {
                this.fetchingCache = false
            });
        },
        selectAllItems() {
            this.$nextTick(() => {
                let parsedItems

                if (this.likesAllApp) {
                    parsedItems = []
                } else {
                    parsedItems = this.attrs.items.slice().map(item => item[this.itemValue])
                }

                this.$emit('input', parsedItems)
            })
        }
    },
    computed: {
        attrs() {
            const { value, items, disabled, ...attrs } = this.$attrs;

            this.setHiddenValues(value)

            return { ...{
                value,
                items: this.mergeItems,
                multiple: this.multiple,
                'item-value': this.itemValue,
                disabled: this.fetchingCache || disabled,
                autocomplete: 'no',
            }, ...attrs }
        },
        hiddenName() {
            return this.multiple ? `${this.name}[]` : this.name
        },
        hasNextPage() {
            return this.lastPage > this.currentPage
        },
        isFirstPage() {
            return this.currentPage == 0
        },
        mergeItems() {
            return _.uniqBy([...this.items, ...this.cache], this.itemValue)
        },
        likesAllApp () {
            return this.items && this.attrs.value && this.attrs.value.length === this.items.length
        },
        likesSomeApp () {
            return this.attrs.value && this.attrs.value.length > 0 && !this.likesAllApp
        },
        icon () {
            if (this.likesAllApp) return '$closeBox'
            if (this.likesSomeApp) return '$minusBox'
            return '$checkboxBlankOutline'
        }
    },
    watch: {
        config: {
            handler() {
                this.triedRecoverIds = false;
                this.resetItems();
            },
            deep: true,
        },
        search() {
            this.debPreSearch()
        },
        rootSearch() {
            if(this.expanded) {
                this.resetItems()
                this.fetchItems()
            }
        },
        'refMenu.isMenuActive'(value) {
            if (value) this.onOpen()
            else this.onClose()
        },
        'refMenu.$refs.menu.listIndex'(value) {
            if (this.multiple && value !== -1) this.refMenu.$refs.menu.listIndex = -1
        },
        '$attrs.value': {
            handler(newVal, oldVal) {
                const hadValue = this.multiple
                    ? (oldVal && oldVal.length > 0)
                    : (oldVal !== null && oldVal !== undefined && oldVal !== '');

                const isEmpty = this.multiple
                    ? (!newVal || newVal.length === 0)
                    : (newVal === null || newVal === undefined || newVal === '');

                if (!hadValue || !isEmpty) {
                    return;
                }

                this.resetSelectOptions();
                this.resetItems();

                if (this.expanded) {
                    this.fetchItems();
                }
            },
        },
    },
}
</script>
